/**
 * Bộ lệnh MDM dựng sẵn — mỗi hàm enqueue một RequestType tới thiết bị.
 * Tất cả đều đi qua operator.enqueue() (thuộc CỤM 1).
 *
 * BẤT ĐỒNG BỘ: các hàm này trả về { command_uuid, ... } ngay lập tức. Kết quả
 * thực tế (thông tin thiết bị, danh sách app/profile) thiết bị trả về SAU qua
 * check-in — đọc lại ở CỤM 2 (query.js), không phải từ giá trị trả về ở đây.
 *
 * Tham chiếu RequestType: developer.apple.com/documentation/devicemanagement
 */

import { enqueue } from './operator';

/**
 * Soi kết quả enqueue (APIResult của NanoMDM). NanoMDM trả HTTP 200 KỂ CẢ khi
 * push/enqueue lỗi — lỗi nằm trong body (command_error = xếp lệnh lỗi;
 * push_error = máy offline, lệnh đã xếp nhưng chưa đánh thức được máy).
 * @returns {{failed?:boolean, offline?:boolean, error?:string}} rỗng nếu OK.
 */
export function enqueueOutcome(res, ids) {
  if (!res || typeof res !== 'object') return {};
  const id = Array.isArray(ids) ? ids[0] : ids;
  const st = (res.status && (res.status[id] || Object.values(res.status)[0])) || {};
  const cmdErr = (res.command_error && res.command_error.error) || (st.command_error && st.command_error.error);
  if (cmdErr) return { failed: true, error: cmdErr };
  const pushErr = (res.push_error && res.push_error.error) || (st.push_error && st.push_error.error);
  if (pushErr) return { offline: true, error: pushErr };
  return {};
}

/** PayloadIdentifier của profile enrollment (xem cmd/mdmquery/enroll.go).
 *  Gỡ profile này = máy thoát khỏi OD Travel MDM (giữ nguyên dữ liệu máy). */
export const ENROLLMENT_PROFILE_ID = 'com.odtravel.mdm';

/** ID cũ (trước khi đổi tên): máy enroll bằng bản cũ vẫn dùng identifier này.
 *  Unenroll gửi RemoveProfile cho cả ID mới lẫn cũ để gỡ được mọi máy. */
export const LEGACY_ENROLLMENT_PROFILE_IDS = ['com.github.micromdm.nanomdm'];

/** PayloadIdentifier của profile Hạn chế (xem cmd/mdmquery/restrict.go).
 *  Dùng để biết máy đã được gán profile Hạn chế hay chưa (từ ProfileList). */
export const RESTRICTIONS_PROFILE_ID = 'com.odtravel.restrictions.profile';

/** Các trường mặc định hỏi khi lấy DeviceInformation. */
export const DEFAULT_INFO_QUERIES = [
  'DeviceName', 'OSVersion', 'BuildVersion', 'ModelName', 'Model',
  'ProductName', 'SerialNumber', 'UDID', 'BatteryLevel',
  'AvailableDeviceCapacity', 'DeviceCapacity', 'WiFiMAC', 'BluetoothMAC',
  'IsSupervised', 'IMEI', 'ICCID',
  'IsMDMLostModeEnabled',
  // 'IsActivationLockEnabled' deprecated từ iOS 16 và hay trả "false" dù Find My/AL
  // đang bật, nên KHÔNG dùng để kết luận "đã tắt". Vẫn hỏi vì chiều DƯƠNG thì đáng
  // tin: server dùng nó chặn bật "ẩn iCloud" khi máy báo AL đang bật (findMyOn()).
  // Trạng thái AL tin cậy để hiển thị vẫn là: có mã bypass hay chưa.
  'IsActivationLockEnabled',
];

export const DeviceCommands = {
  // ── Truy vấn thông tin (kết quả đọc lại ở CỤM 2) ──────────────────────────
  deviceInformation: (udid, queries = DEFAULT_INFO_QUERIES, opts) =>
    enqueue(udid, { RequestType: 'DeviceInformation', Queries: queries }, opts),

  installedApplications: (udid, opts) =>
    enqueue(udid, { RequestType: 'InstalledApplicationList' }, opts),

  profileList: (udid, opts) =>
    enqueue(udid, { RequestType: 'ProfileList' }, opts),

  securityInfo: (udid, opts) =>
    enqueue(udid, { RequestType: 'SecurityInfo' }, opts),

  // ── Điều khiển thiết bị ───────────────────────────────────────────────────
  /** Khóa thiết bị (kèm message/PIN tùy chọn). */
  lock: (udid, { message, pin } = {}) =>
    enqueue(udid, {
      RequestType: 'DeviceLock',
      ...(message && { Message: message }),
      ...(pin && { PIN: pin }),
    }),

  clearPasscode: (udid) =>
    enqueue(udid, { RequestType: 'ClearPasscode' }),

  restart: (udid) =>
    enqueue(udid, { RequestType: 'RestartDevice' }),

  shutdown: (udid) =>
    enqueue(udid, { RequestType: 'ShutDownDevice' }),

  scheduleOSUpdate: (udid, version, installAction = 'InstallASAP') =>
    enqueue(udid, {
      RequestType: 'ScheduleOSUpdate',
      Updates: [{ ProductVersion: version, InstallAction: installAction }],
    }),

  // ── Profiles ──────────────────────────────────────────────────────────────
  installProfile: (udid, profileBase64) =>
    enqueue(udid, { RequestType: 'InstallProfile', Payload: profileBase64 }),

  removeProfile: (udid, identifier) =>
    enqueue(udid, { RequestType: 'RemoveProfile', Identifier: identifier }),

  /** Gỡ máy khỏi OD Travel MDM: gỡ chính profile enrollment (unenroll, KHÔNG xóa dữ liệu).
   *  Máy sẽ gửi CheckOut về server (CheckOutWhenRemoved) -> enrollment chuyển enabled=false.
   *  Gửi RemoveProfile cho cả ID mới lẫn ID cũ (RemoveProfile profile không có = vô hại)
   *  để gỡ được cả máy enroll trước và sau khi đổi tên identifier. */
  unenroll: (udid) =>
    Promise.all([ENROLLMENT_PROFILE_ID, ...LEGACY_ENROLLMENT_PROFILE_IDS].map(
      id => enqueue(udid, { RequestType: 'RemoveProfile', Identifier: id }))),

  // ── Ứng dụng ────────────────────────────────────────────────────────────────
  installApp: (udid, { iTunesStoreID, manifestURL } = {}) =>
    enqueue(udid, {
      RequestType: 'InstallApplication',
      ...(iTunesStoreID && { iTunesStoreID }),
      ...(manifestURL && { ManifestURL: manifestURL }),
    }),

  removeApp: (udid, identifier) =>
    enqueue(udid, { RequestType: 'RemoveApplication', Identifier: identifier }),

  // ── Lost Mode / vị trí / âm thanh ─────────────────────────────────────────
  playSound: (udid) =>
    enqueue(udid, { RequestType: 'PlayLostModeSound' }),

  enableLostMode: (udid, { message, phoneNumber, footnote } = {}) =>
    enqueue(udid, {
      RequestType: 'EnableLostMode',
      ...(message && { Message: message }),
      ...(phoneNumber && { PhoneNumber: phoneNumber }),
      ...(footnote && { Footnote: footnote }),
    }),

  disableLostMode: (udid) =>
    enqueue(udid, { RequestType: 'DisableLostMode' }),

  deviceLocation: (udid) =>
    enqueue(udid, { RequestType: 'DeviceLocation' }),

  // ── Settings ──────────────────────────────────────────────────────────────
  setDeviceName: (udid, name) =>
    enqueue(udid, { RequestType: 'Settings', Settings: [{ Item: 'DeviceName', DeviceName: name }] }),

  setWallpaper: (udid, imageBase64, where = 3) =>
    enqueue(udid, {
      RequestType: 'Settings',
      Settings: [{ Item: 'Wallpaper', Image: imageBase64, Where: where }],
    }),
};
